import React from 'react';
import styled from 'styled-components';
import { pageShimmerShapes, shimmerFill } from '../../motion';
import { HubNewGuidesRow } from './HubNewGuidesRow';
import { GuideProductGrid } from '../guide/GuideProductCard';
import { HubDisclosure } from './HubFrameParts';
import { HUB_GUTTER_CSS, HUB_HEADING_CSS } from './hubType';

const Page = styled.div`background: white; padding-bottom: 32px;`;
const Gutter = styled.div<{ $wideGutter?: boolean }>`${HUB_GUTTER_CSS} padding-top: 24px;`;
const Shape = styled.div<{ $shape: keyof typeof pageShimmerShapes }>`
  ${shimmerFill}
  ${({ $shape }: { $shape: keyof typeof pageShimmerShapes }) => pageShimmerShapes[$shape]}
`;
const Top = styled.div`
  display: grid; gap: 20px; margin-bottom: 24px;
  @media (min-width: 1024px) { grid-template-columns: auto 1fr; align-items: center; }
`;
const Banner = styled.div`
  padding: 32px 0; background: #f5e9e5;
`;
const BannerInset = styled.div<{ $wideGutter?: boolean }>`${HUB_GUTTER_CSS} display: grid; gap: 16px;`;
const Occasions = styled.div`
  display: grid; grid-template-rows: repeat(2, 76px); grid-auto-flow: column;
  grid-auto-columns: 240px; gap: 12px; overflow: hidden; margin-bottom: 28px;
`;
const Rail = styled.div`
  display: grid; grid-auto-flow: column; grid-auto-columns: 160px;
  gap: 16px; overflow: hidden; margin: 20px 0 28px;
`;
const Products = styled.div<{ $guide: boolean }>`
  display: grid; grid-template-columns: ${({ $guide }) => $guide ? '1fr' : 'repeat(2, 1fr)'};
  gap: 20px; margin-top: 24px;
  @media (min-width: 1024px) { grid-template-columns: repeat(4, 1fr); }
`;
const SearchSummary = styled.div<{ $wideGutter?: boolean }>`${HUB_GUTTER_CSS} padding-top: 20px; padding-bottom: 16px;`;
const SearchGhostRow = styled.div`
  pointer-events: none;
  h2 { ${shimmerFill} color: transparent; width: 220px; border-radius: 8px; }
  a { ${shimmerFill} box-shadow: none; }
  a > div, a p { visibility: hidden; }
  button { visibility: hidden; }
`;
const SearchControls = styled.div<{ $wideGutter?: boolean }>`${HUB_GUTTER_CSS} padding-top: 20px;`;
const SearchHeading = styled.div`${HUB_HEADING_CSS} ${shimmerFill} color: transparent; width: 220px; border-radius: 8px;`;
const SearchPills = styled.div`
  display: flex; gap: 12px; margin-top: 12px; padding: 8px 0;
  > div { width: 120px; height: 40px; border-radius: 999px; }
`;
const SearchDisclosure = styled.div`p { color: transparent; }`;
const Month = styled.div`display: grid; grid-template-columns: repeat(7, 1fr); gap: 8px; margin: 24px 0;`;
const Lines = styled.div`display: grid; gap: 20px; margin-top: 24px;`;
const cards = (count: number) => Array.from({ length: count }, (_, i) => <Shape key={i} $shape="product" />);
export function HubPageSkeleton({ page = 'hub', wideGutterEnabled = false }: { page?: 'search' | 'hub' | 'collection' | 'guide' | 'people' | 'calendar' | 'you'; wideGutterEnabled?: boolean }) {
  if (page === 'search') return <Page role="status" aria-label="Loading search" data-testid="hub-skeleton-search">
    <div aria-hidden="true" {...{ inert: '' }}>
      <SearchSummary $wideGutter={wideGutterEnabled}><Shape $shape="title" style={{ height: 21, width: 180 }} /></SearchSummary>
      <SearchGhostRow><HubNewGuidesRow heading="Guides" headingId="search-skeleton-guides" badge={false}
        sections={Array.from({ length: 3 }, (_, i) => ({ key: String(i), href: '#', title: 'Loading guide', emoji: '', imageUrls: [] }))} wideGutterEnabled={wideGutterEnabled} /></SearchGhostRow>
      <SearchControls $wideGutter={wideGutterEnabled}><SearchHeading>Gifts</SearchHeading><SearchPills><Shape $shape="search" /><Shape $shape="search" /></SearchPills></SearchControls>
      <SearchDisclosure><HubDisclosure wideGutterEnabled={wideGutterEnabled} /></SearchDisclosure>
      <GuideProductGrid $hubFrame $wideGutter={wideGutterEnabled} data-testid="search-skeleton-products">{cards(4)}</GuideProductGrid>
    </div>
  </Page>;
  const banner = page === 'guide' || page === 'collection';
  return <Page role="status" aria-label={`Loading ${page === 'hub' ? 'gift guides' : page}`} data-testid={`hub-skeleton-${page}`}>
    <div aria-hidden="true">
      {banner && <Banner><BannerInset $wideGutter={wideGutterEnabled}><Shape $shape="title" /><Shape $shape="search" /></BannerInset></Banner>}
      <Gutter $wideGutter={wideGutterEnabled}>
        {!banner && (page === 'hub' ? <Top><Shape $shape="title" /><Shape $shape="search" /></Top> : <Shape $shape="title" />)}
        {page === 'hub' && <><Occasions>{Array.from({ length: 10 }, (_, i) => <Shape key={i} $shape="occasion" />)}</Occasions>
          <Shape $shape="title" /><Rail>{cards(8)}</Rail><Shape $shape="title" /></>}
        {page === 'calendar' ? <><Month>{cards(35)}</Month><Lines><Shape $shape="search" /><Shape $shape="search" /></Lines></>
          : page === 'you' ? <Lines><Shape $shape="banner" /><Shape $shape="search" /><Shape $shape="search" /></Lines>
          : <Products $guide={page === 'guide'}>{cards(page === 'guide' ? 4 : 8)}</Products>}
      </Gutter>
    </div>
  </Page>;
}
